import React, { useRef } from 'react';
import { motion, useScroll, useTransform, Variants } from 'framer-motion';
import { ScrollReveal } from '../TextType';

export const TechHero: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  // const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  // const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  const textVariants: Variants = {
    hidden: { opacity: 0, y: 100 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 1,
        ease: "easeOut",
      }
    })
  };

  return (
    <section ref={containerRef} className="relative w-full min-h-screen bg-black flex flex-col justify-between pt-24 md:pt-32 overflow-hidden">
      
      {/* 1. Top Text Content */}
      <div className="relative z-10 flex flex-col items-center justify-center text-center px-4 mb-12">
        <motion.div
           initial="hidden"
           animate="visible"
           className="flex flex-col items-center"
        >
          {/* Top Images Floating Effect - Made Smaller */}
          <motion.div 
            className="mb-8 flex justify-center perspective-500"
            variants={textVariants}
            custom={0}
          >
             <div className="flex -space-x-4 items-center">
                  <motion.img 
                    src="/Images/tech/tech_1.webp" 
                    alt="Technology Feature 1"
                    className="w-12 h-16 object-cover rounded-lg border border-white/10 shadow-2xl mix-blend-overlay opacity-60"
                    animate={{ rotateY: [-15, -5, -15], y: [0, -10, 0] }}
                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                 />
                 <motion.img 
                    src="/Images/tech/tech_2.webp" 
                    alt="Technology Feature 2"
                    className="w-16 h-20 object-cover rounded-lg border border-white/20 shadow-2xl z-20"
                    animate={{ y: [0, -15, 0] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                 />
                 <motion.img 
                    src="/Images/tech/tech_3.webp" 
                    alt="Technology Feature 3"
                    className="w-12 h-16 object-cover rounded-lg border border-white/10 shadow-2xl mix-blend-overlay opacity-60"
                    animate={{ rotateY: [15, 5, 15], y: [0, -10, 0] }}
                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                 />
             </div>
          </motion.div>

          <div className="overflow-hidden">
            <ScrollReveal
              textClassName="text-white"
              containerClassName="max-w-5xl px-4"
              size="lg"
              align="center"
              enableBlur={true}
              baseOpacity={0.2}
              blurStrength={6}
              staggerDelay={0.03}
              threshold={0.3}
              duration={0.6}
            >
              Advanced systems powering premium mono-carton production.
            </ScrollReveal>
          </div>
        </motion.div>
      </div>

      {/* 2. Video Layer */}
      <motion.div 
        className="relative w-full h-[50vh] md:h-[60vh] overflow-hidden"
        style={{ scale }}
      >
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover opacity-80"
        >
          <source src="/Images/tech/tech_v.mp4" type="video/mp4" />
        </video>
        {/* Gradients to blend video edges */}
        <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-black to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-black to-transparent" />
      </motion.div>

      {/* 3. Bottom PrintAura Text */}
      <div className="relative z-20 w-full flex justify-center items-end -mt-16 md:-mt-32 pointer-events-none pb-0">
         <h1
            style={{
                fontFamily: 'Nanum Myeongjo, serif',
                fontWeight: 800,
                fontSize: 'clamp(42px, 15vw, 303px)', // Responsive clamp but targeting the large size
                lineHeight: '1.1',
                letterSpacing: '0%',
                background: 'linear-gradient(180deg, #FFFFFF 34.49%, rgba(255, 255, 255, 0.12) 71.55%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
            }}
            className="text-center"
         >
            PrintAura
         </h1>
      </div>

    </section>
  );
};
